import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Activity, Users, Building2, Database, AlertCircle } from 'lucide-react';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DashboardHome = () => {
  const { superAdmin } = useSuperAdminAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [categoryCount, setCategoryCount] = useState(null);
  const [loading, setLoading] = useState(true);

  const apiRef = useRef(null);
  if (!apiRef.current) {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use((config) => {
      const token =
        superAdmin?.token ||
        localStorage.getItem('superadmin_session') ||
        localStorage.getItem('superadmin_session_token') ||
        localStorage.getItem('superAdminToken') ||
        localStorage.getItem('superadminToken');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    apiRef.current = instance;
  }
  const api = apiRef.current;

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [dashResp, catResp] = await Promise.all([
          api.get('/api/v1/super-admin/dashboard'),
          api.get('/api/v1/superadmin/taxonomy/feedback-categories'),
        ]);
        setDashboardData(dashResp.data);
        setCategoryCount(Array.isArray(catResp.data) ? catResp.data.length : 0);
      } catch {
        // Non-fatal — show unavailable state
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  const fmt = (val) => (val === null || val === undefined ? '—' : String(val));

  // Honest health projection — UNKNOWN/UNAVAILABLE/DEGRADED never read as operational.
  const HEALTH_LABEL = { healthy: 'Healthy', degraded: 'Degraded', unavailable: 'Unavailable', unknown: 'Unknown' };
  const healthState = dashboardData?.system_health?.state;
  const healthLabel = loading ? '…' : (HEALTH_LABEL[healthState] || 'Unknown');
  const bannerState = () => {
    if (loading) return 'loading';
    if (!healthState || healthState === 'unknown') return 'unknown';
    if (healthState === 'degraded' || healthState === 'unavailable') return 'degraded';
    if ((dashboardData?.critical_alerts || 0) > 0) return 'alerts';
    if (healthState === 'healthy') return 'operational';
    return 'unknown';
  };
  const banner = bannerState();

  const stats = [
    {
      icon: Building2,
      label: 'Total Businesses',
      value: loading ? '…' : fmt(dashboardData?.total_tenants),
      color: 'bg-blue-500'
    },
    {
      icon: Users,
      label: dashboardData?.active_users_label || 'Active Administrators',
      value: loading ? '…' : fmt(dashboardData?.active_users),
      color: 'bg-green-500'
    },
    {
      icon: Database,
      label: 'Feedback Categories',
      value: loading ? '…' : fmt(categoryCount),
      color: 'bg-purple-500'
    },
    {
      icon: Activity,
      label: 'System Health',
      value: healthLabel,
      color: 'bg-emerald-500'
    }
  ];

  const recentActivities = dashboardData?.recent_activities || [];

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600 mt-1">Welcome to the Super Admin Control Portal</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-4 rounded-lg`}>
                <stat.icon className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity + System Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Activities</h2>
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : recentActivities.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No recent activity recorded.</p>
          ) : (
            <div className="space-y-4">
              {recentActivities.map((activity, index) => (
                <div key={index} className="flex items-center gap-3 pb-3 border-b border-gray-100 last:border-0">
                  <Activity className="h-5 w-5 text-purple-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{activity.message || activity.type || 'System event'}</p>
                    <p className="text-xs text-gray-500">{activity.timestamp || ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">System Alerts</h2>
          {/* Honest banner: 'All systems operational' ONLY when health is known-healthy AND no critical
              alerts. Unknown/degraded/loading each get a truthful state — never a false success. */}
          {banner === 'loading' ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : banner === 'operational' ? (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-green-600" />
              <p className="text-sm text-green-700">All systems operational — no critical alerts.</p>
            </div>
          ) : banner === 'alerts' ? (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600" />
              <p className="text-sm text-red-700">{dashboardData.critical_alerts} critical alert(s) require attention.</p>
            </div>
          ) : banner === 'degraded' ? (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-600" />
              <p className="text-sm text-amber-700">Platform health is degraded — see Control Tower.</p>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-gray-400" />
              <p className="text-sm text-gray-500">Platform health status is unavailable.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
